import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
    Typography,
    Button,
    Alert,
    AlertTitle,
    Box,
    CircularProgress,
    Collapse,
    Slide,
    Skeleton,
    Grid,
    Card,
    CardContent,
    CardActions,
    Chip,
    Avatar
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InstagramIcon from "@mui/icons-material/Instagram";
import VideocamIcon from "@mui/icons-material/Videocam";
import PodcastsIcon from "@mui/icons-material/Podcasts";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import TabPanelHidden from "../../../../Components/TabPanelHidden";
import PlatformDialog from "../../../../Components/CreatorData/creatorPlatforms";
import API from "../../../../API";
import { parseLocalFloat, parseLocalInt } from "../../../../Utils/constants";
import { Instagram, TikTok, YouTube, Twitch } from "../../../../Utils/icons";

// Platform configurations
const PLATFORM_CONFIGS = {
  Instagram: {
    icon: <Instagram  width={40}
    height={40}/>,
    color: '#ffffff',
    prefix: 'instagram.com/',
    emoji: '📸',
    fields: [
      { key: 'followerCount', label: 'Follower Count', type: 'number' },
      { key: 'brandRate', label: 'Brand Rate', type: 'rate', minValue: 100 },
      { key: 'songRate', label: 'Song Rate', type: 'rate', minValue: 100 },
      { key: 'feedRate', label: 'Feed Post Rate', type: 'rate', minValue: 100 },
      { key: 'storyRate', label: 'Story Rate', type: 'rate', minValue: 20 }
    ]
  },
  TikTok: {
    icon: <TikTok  width={40}
    height={40} />,
    color: '#000000',
    prefix: 'tiktok.com/@',
    emoji: '🎵',
    fields: [
      { key: 'followerCount', label: 'Follower Count', type: 'number' },
      { key: 'brandRate', label: 'Brand Rate', type: 'rate', minValue: 100 },
      { key: 'songRate', label: 'Song Rate', type: 'rate', minValue: 100 }
    ]
  },
  YouTube: {
    icon: <YouTube  width={40}
    height={40} />,
    color: '#FFFFFF',
    prefix: 'youtube.com/',
    emoji: '▶️',
    fields: [
      { key: 'followerCount', label: 'Subscriber Count', type: 'number' },
      { key: 'integrationRate', label: 'Brand Integration Rate', type: 'rate', minValue: 100 },
      { key: 'shortsRate', label: 'Shorts Rate', type: 'rate', minValue: 100 },
      { key: 'dedicatedRate', label: 'Dedicated Video Rate', type: 'rate', minValue: 100 }
    ]
  },
  X: {
    icon: <span style={{ fontWeight: 'bold' }}>𝕏</span>,
    color: '#000000',
    prefix: 'x.com/',
    emoji: '𝕏',
    fields: [
      { key: 'followerCount', label: 'Follower Count', type: 'number' },
      { key: 'feedPost', label: 'Feed Post Rate', type: 'rate', minValue: 100 },
      { key: 'repost', label: 'Repost Rate', type: 'rate', minValue: 100 }
    ]
  },
  Podcast: {
    icon: <PodcastsIcon />,
    color: '#9C27B0',
    prefix: '',
    emoji: '🎙️',
    isUrl: true,
    fields: [
      { key: 'followerCount', label: 'Listener Count', type: 'number' },
      { key: 'preroll', label: 'Preroll Ad Rate', type: 'rate', minValue: 100 },
      { key: 'midroll', label: 'Midroll Ad Rate', type: 'rate', minValue: 100 },
      { key: 'postroll', label: 'Postroll Ad Rate', type: 'rate', minValue: 100 },
      { key: 'full', label: 'Full Episode Sponsorship Rate', type: 'rate', minValue: 100 }
    ]
  },
  Kick: {
    icon: <SportsEsportsIcon />,
    color: '#53FC18',
    prefix: 'kick.com/',
    emoji: '🎮',
    fields: [
      { key: 'followerCount', label: 'Follower Count', type: 'number' },
      { key: 'placement', label: 'Product Placement Rate', type: 'rate', minValue: 100 }
    ]
  },
  Twitch: {
    icon: <Twitch width={40}
    height={40} />,
    color: '#fff',
    prefix: 'twitch.tv/',
    emoji: '🟣',
    fields: [
      { key: 'followerCount', label: 'Follower Count', type: 'number' },
      { key: 'placement', label: 'Product Placement Rate', type: 'rate', minValue: 100 }
    ]
  }
};

// Build API payload for specific platform - moved outside component
const buildPlatformPayload = (platform, data) => {
  const payload = {};
 
  switch(platform) {
    case 'Instagram':
      payload.instagram = parseLocalInt(data.followerCount) || null;
      payload.instagram_link = data.link ? `https://www.instagram.com/${data.link}` : null;
      payload.rate_ig_reelbrand = parseLocalFloat(data.brandRate);
      payload.rate_ig_reelsound = parseLocalFloat(data.songRate);
      payload.rate_ig_feedpost = parseLocalFloat(data.feedRate);
      payload.rate_ig_story = parseLocalFloat(data.storyRate);
      break;
    case 'TikTok':
      payload.tiktok = parseLocalInt(data.followerCount) || null;
      payload.tiktok_link = data.link ? `https://www.tiktok.com/@${data.link}` : null;
      payload.rate_tt_brand = parseLocalFloat(data.brandRate);
      payload.rate_tt_sound = parseLocalFloat(data.songRate);
      break;
    case 'YouTube':
      payload.youtube = parseLocalInt(data.followerCount) || null;
      payload.youtube_link = data.link ? `https://www.youtube.com/${data.link}` : null;
      payload.rate_yt_integ = parseLocalFloat(data.integrationRate);
      payload.rate_yt_short = parseLocalFloat(data.shortsRate);
      payload.rate_yt_dedicated = parseLocalFloat(data.dedicatedRate);
      break;
    case 'X':
      payload.follow_x = parseLocalInt(data.followerCount) || null;
      payload.x_link = data.link ? `https://www.x.com/${data.link}` : null;
      payload.rate_x_feedpost = parseLocalFloat(data.feedPost);
      payload.rate_x_repost = parseLocalFloat(data.repost);
      break;
    case 'Podcast':
      payload.follow_podcast = parseLocalInt(data.followerCount) || null;
      payload.podcast_link = data.link || null;
      payload.rate_podcast_preroll = parseLocalFloat(data.preroll);
      payload.rate_podcast_midroll = parseLocalFloat(data.midroll);
      payload.rate_podcast_postroll = parseLocalFloat(data.postroll);
      payload.rate_podcast_full = parseLocalFloat(data.full);
      break;
    case 'Kick':
      payload.follow_kick = parseLocalInt(data.followerCount) || null;
      payload.kick_link = data.link ? `https://kick.com/${data.link}` : null;
      payload.rate_kick_product = parseLocalFloat(data.placement);
      break;
    case 'Twitch':
      payload.twitch = parseLocalInt(data.followerCount) || null;
      payload.twitch_link = data.link ? `https://www.twitch.tv/${data.link}` : null;
      payload.rate_twitch_product = parseLocalFloat(data.placement);
      break;
  }
 
  return payload;
};

function PlatformsTab({
  tabValue,
  index,
  showPlatformsTip,
  onSaveSuccess,
  onSaveError,
  onDataUpdate,
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
 
  // Platform data storage
  const [platformData, setPlatformData] = useState({});
 
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [dialogData, setDialogData] = useState({});
  const [dialogSaving, setDialogSaving] = useState(false);
 
  const initialValuesRef = useRef(null);
  const lastSummaryRef = useRef(null);

  // Calculate summary data - memoized
  const platformSummary = useMemo(() => {
    const activePlatforms = Object.keys(platformData).filter(p => platformData[p]?.active);
    const totalFollowers = activePlatforms.reduce((sum, platform) => {
      const count = parseInt(platformData[platform]?.followerCount || 0);
      return sum + count;
    }, 0);
    
    return {
      totalFollowers,
      activePlatforms: activePlatforms.length,
      platformData
    };
  }, [platformData]);

  // Update parent only when summary actually changes
  useEffect(() => {
    if (onDataUpdate && platformSummary) {
      const summaryStr = JSON.stringify({
        totalFollowers: platformSummary.totalFollowers,
        activePlatforms: platformSummary.activePlatforms
      });
      
      if (lastSummaryRef.current !== summaryStr) {
        lastSummaryRef.current = summaryStr;
        onDataUpdate(platformSummary);
      }
    }
  }, [platformSummary, onDataUpdate]);
  
  // Fetch platform data - stable callback
  const fetchPlatformData = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
     
      const response = await API.creatorConnect.getCreator();
      const cr = response.user.creator;
     
      // Build platform data object
      const data = {
        Instagram: {
          link: cr.instagram_link?.replace("https://www.instagram.com/", "") || "",
          followerCount: cr.instagram ? cr.instagram.toString() : "",
          brandRate: cr.rate_ig_reelbrand?.toFixed(2) || "",
          songRate: cr.rate_ig_reelsound?.toFixed(2) || "",
          feedRate: cr.rate_ig_feedpost?.toFixed(2) || "",
          storyRate: cr.rate_ig_story?.toFixed(2) || "",
          active: !!cr.instagram_link
        },
        TikTok: {
          link: cr.tiktok_link?.replace("https://www.tiktok.com/@", "") || "",
          followerCount: cr.tiktok ? cr.tiktok.toString() : "",
          brandRate: cr.rate_tt_brand?.toFixed(2) || "",
          songRate: cr.rate_tt_sound?.toFixed(2) || "",
          active: !!cr.tiktok_link
        },
        YouTube: {
          link: cr.youtube_link?.replace("https://www.youtube.com/", "") || "",
          followerCount: cr.youtube ? cr.youtube.toString() : "",
          integrationRate: cr.rate_yt_integ?.toFixed(2) || "",
          shortsRate: cr.rate_yt_short?.toFixed(2) || "",
          dedicatedRate: cr.rate_yt_dedicated?.toFixed(2) || "",
          active: !!cr.youtube_link
        },
        X: {
          link: cr.x_link?.replace("https://x.com/", "") || "",
          followerCount: cr.follow_x ? cr.follow_x.toString() : "",
          feedPost: cr.rate_x_feedpost?.toFixed(2) || "",
          repost: cr.rate_x_repost?.toFixed(2) || "",
          active: !!cr.x_link
        },
        Podcast: {
          link: cr.podcast_link || "",
          followerCount: cr.follow_podcast ? cr.follow_podcast.toString() : "",
          preroll: cr.rate_podcast_preroll?.toFixed(2) || "",
          midroll: cr.rate_podcast_midroll?.toFixed(2) || "",
          postroll: cr.rate_podcast_postroll?.toFixed(2) || "",
          full: cr.rate_podcast_full?.toFixed(2) || "",
          active: !!cr.podcast_link
        },
        Kick: {
          link: cr.kick_link?.replace("https://kick.com/", "") || "",
          followerCount: cr.follow_kick ? cr.follow_kick.toString() : "",
          placement: cr.rate_kick_product?.toFixed(2) || "",
          active: !!cr.kick_link
        },
        Twitch: {
          link: cr.twitch_link?.replace("https://www.twitch.tv/", "") || "",
          followerCount: cr.twitch ? cr.twitch.toString() : "",
          placement: cr.rate_twitch_product?.toFixed(2) || "",
          active: !!cr.twitch_link
        }
      };
     
      setPlatformData(data);
      initialValuesRef.current = JSON.parse(JSON.stringify(data));
     
    } catch (err) {
      const errorMsg = err?.response?.data?.error || err?.response?.data?.details || err.message;
      setLoadError(errorMsg);
      if (onSaveError) {
        onSaveError(errorMsg);
      }
    } finally {
      setIsLoading(false);
    }
  }, []); // No dependencies - stable callback
  
  useEffect(() => {
    if (tabValue === index) {
      fetchPlatformData();
    }
  }, [tabValue, index, fetchPlatformData]);
  
  // Open platform dialog - stable callback
  const handleOpenDialog = useCallback((platform) => {
    setSelectedPlatform(platform);
    setDialogData(platformData[platform] || {});
    setDialogOpen(true);
  }, [platformData]);
  
  // Close dialog - stable callback
  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedPlatform(null);
    setDialogData({});
    setDialogSaving(false);
  }, []);
  
  // Save platform data - stable callback
  const handleSavePlatform = useCallback(async (platform, data) => {
    try {
      setDialogSaving(true);
     
      const payload = buildPlatformPayload(platform, data);
      const isPlatformActive = Boolean(data.link);
     
      const response = await API.creatorConnect.editCreator(payload);
     
      // Update local state
      setPlatformData(prev => ({
        ...prev,
        [platform]: { ...data, active: isPlatformActive }
      }));
     
      // Update initial values
      if (initialValuesRef.current) {
        initialValuesRef.current[platform] = { ...data, active: isPlatformActive };
      }
     
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 5000);
     
      if (onSaveSuccess) {
        const updatedFields = response?.updated_fields || [];
        onSaveSuccess(updatedFields.length > 0 ? `Updated ${platform}: ${updatedFields.join(', ')}` : `${platform} saved successfully!`);
      }
     
      handleCloseDialog();
     
    } catch (err) {
      if (onSaveError) {
        onSaveError(err?.response?.data?.error || err?.response?.data?.details || err.message);
      }
    } finally {
      setDialogSaving(false);
    }
  }, [onSaveSuccess, onSaveError, handleCloseDialog]);

  const handleClearPlatform = useCallback(async (platform, data) => {
    try {
      setDialogSaving(true);

      const payload = buildPlatformPayload(platform, data);
      await API.creatorConnect.editCreator(payload);

      setPlatformData(prev => ({
        ...prev,
        [platform]: { ...data, active: false }
      }));

      if (initialValuesRef.current) {
        initialValuesRef.current[platform] = { ...data, active: false };
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 5000);

      if (onSaveSuccess) {
        onSaveSuccess(`${platform} cleared successfully!`);
      }

      handleCloseDialog();
    } catch (err) {
      if (onSaveError) {
        onSaveError(err?.response?.data?.error || err?.response?.data?.details || err.message);
      }
    } finally {
      setDialogSaving(false);
    }
  }, [onSaveSuccess, onSaveError, handleCloseDialog]);

  const platformKeys = Object.keys(PLATFORM_CONFIGS);
  const activePlatforms = Object.keys(platformData).filter(p => platformData[p]?.active);

  // Loading skeleton
  if (isLoading) {
    return (
      <TabPanelHidden value={tabValue} index={index} id={`tab-contents-${index}`}>
        <Box sx={{ maxWidth: '1200px', margin: '0 auto' }}>
          <Skeleton variant="rectangular" height={150} sx={{ borderRadius: '16px', mb: 3 }} />
          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '12px' }} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </TabPanelHidden>
    );
  }
  
  // Error state
  if (loadError) {
    return (
      <TabPanelHidden value={tabValue} index={index} id={`tab-contents-${index}`}>
        <Box sx={{ maxWidth: '1200px', margin: '0 auto' }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            <AlertTitle>Failed to Load Platform Data</AlertTitle>
            {loadError}
            <Button
              variant="outlined"
              size="small"
              onClick={fetchPlatformData}
              sx={{ mt: 2 }}
            >
              Retry
            </Button>
          </Alert>
        </Box>
      </TabPanelHidden>
    );
  }
  
  return (
    <TabPanelHidden value={tabValue} index={index} id={`tab-contents-${index}`}>
      <Box sx={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <Box sx={{
      
          marginBottom: '32px', 
          // boxShadow: '0 8px 24px rgba(102, 126, 234, 0.25)',
        }}>
          <Typography variant="h4" sx={{
            color: 'black',
            fontWeight: 700,
            mb: 1,
            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            Platforms & Rates
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(30,30,30,0.9)', mb: 2 }}>
            Configure your social media presence and monetization rates
          </Typography>
          <Chip
            label={`${activePlatforms.length} Active Platform${activePlatforms.length !== 1 ? 's' : ''}`}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'black',
              fontWeight: 600
            }}
          />
        </Box>
        
        {/* Success Message */}
        <Collapse in={saveSuccess}>
          <Slide direction="down" in={saveSuccess}>
            <Alert
              severity="success"
              sx={{
                mb: 3,
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
              icon={<CheckCircleIcon fontSize="large" />}
            >
              <AlertTitle sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                Changes Saved Successfully!
              </AlertTitle>
              Your platform information has been updated.
            </Alert>
          </Slide>
        </Collapse>
        
        {/* Warning if no platforms */}
        {activePlatforms.length === 0 && (
          <Alert
            severity="warning"
            sx={{
              mb: 3,
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
            icon={<WarningAmberIcon fontSize="large" />}
          >
            <AlertTitle sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
              No Platforms Configured
            </AlertTitle>
            Add your first platform to showcase your creator presence and start receiving collaboration opportunities.
          </Alert>
        )}
        
        {/* Platform Cards Grid */}
        <Grid container spacing={3}>
          {platformKeys.map((platform) => {
            const config = PLATFORM_CONFIGS[platform];
            const data = platformData[platform] || {};
            const isActive = data.active;
           
            return (
              <Grid item xs={12} sm={6} md={4} key={platform}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '12px',
                    border: isActive ? `2px solid ${config.color}` : '2px solid #e0e0e0',
                    boxShadow: isActive ? '0 4px 12px rgba(0,0,0,0.15)' : '0 2px 8px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                      transform: 'translateY(-4px)'
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
                      <Avatar sx={{
                        bgcolor: config.color,
                        width: 48,
                        height: 48,
                        fontSize: '1.5rem'
                      }}>
                        {config.icon}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {platform}
                        </Typography>
                        {isActive && data.followerCount && (
                          <Typography variant="body2" color="text.secondary">
                            {parseInt(data.followerCount).toLocaleString()} followers
                          </Typography>
                        )}
                      </Box>
                      {isActive && (
                        <CheckCircleIcon sx={{ color:"green" }} />
                      )}
                    </Box>
                   
                    {isActive ? (
                      <Box>
                        <Chip
                          label="Active"
                          size="small"
                          sx={{
                            backgroundColor: `green20`,
                            color: "green",
                            fontWeight: 600,
                            mb: 1
                          }}
                        />
                        {data.link && (
                          <Typography variant="body2" color="text.secondary" sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {config.prefix}{data.link}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Click to add this platform and configure your rates
                      </Typography>
                    )}
                  </CardContent>
                 
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      fullWidth
                      variant={isActive ? "outlined" : "contained"}
                      startIcon={isActive ? <EditIcon /> : <AddCircleOutlineIcon />}
                      onClick={() => handleOpenDialog(platform)}
                      sx={{
                        borderColor: isActive ? "gray10" : undefined,
                        color: isActive ? "gray10" : 'white',
                        backgroundColor: isActive ? 'transparent' : "gray10",
                        '&:hover': {
                          backgroundColor: isActive ? `gray20` : undefined,
                          borderColor: "gray20"
                        }
                      }}
                    >
                      {isActive ? 'Edit Details' : 'Add Platform'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
      
      {/* Platform Dialog */}
      {selectedPlatform && dialogOpen && (
        <PlatformDialog
          open={dialogOpen}
          platform={selectedPlatform}
          config={PLATFORM_CONFIGS[selectedPlatform]}
          data={dialogData}
          saving={dialogSaving}
          onClose={handleCloseDialog}
          onSave={handleSavePlatform}
          onClear={handleClearPlatform}
        />
      )}
    </TabPanelHidden>
  );
}

export default React.memo(PlatformsTab);
